import { format } from "date-fns";
import type { Identifier, RaRecord } from "ra-core";

export type DeliveryPhase = "not_started" | "in_progress" | "completed";

export type Delivery = RaRecord & {
  title: string;
  deal_id?: Identifier | null;
  company_id?: Identifier | null;
  current_position: number;
  phase: DeliveryPhase;
};

export type Milestone = RaRecord & {
  delivery_id: Identifier;
  position: number;
  name: string;
  due_date?: string | null;
};

export type Invoice = RaRecord & {
  number: string;
  company_id: number;
  deal_id?: number | null;
  issue_date?: string | null;
  due_date?: string | null;
  amount: number;
  currency: string;
  amount_paid: number;
  status: "draft" | "sent" | "partially_paid" | "paid" | "cancelled";
  notes?: string | null;
  collected_by?: number | null;
  milestone_id?: Identifier | null;
};

export const deliveryPhaseChoices = [
  "not_started",
  "in_progress",
  "completed",
].map((id) => ({ id, name: `resources.deliveries.phases.${id}` }));

/** The phase the database trigger derives from the position: first, last or in between */
export const deliveryPhase = (
  position: number,
  milestoneCount: number,
): DeliveryPhase =>
  position === 0
    ? "not_started"
    : position === milestoneCount - 1
      ? "completed"
      : "in_progress";

export const invoiceStatusChoices = [
  "draft",
  "sent",
  "partially_paid",
  "paid",
  "cancelled",
].map((id) => ({ id, name: `resources.invoices.statuses.${id}` }));

/** Today as a local yyyy-MM-dd, the format of the date columns */
export const today = () => format(new Date(), "yyyy-MM-dd");

/** Overdue = due before today and neither paid nor cancelled (computed, never stored) */
export const isOverdue = (
  invoice: { due_date?: string | null; status: string },
  now = today(),
) =>
  !!invoice.due_date &&
  invoice.due_date < now &&
  !["paid", "cancelled"].includes(invoice.status);

/** The same rule as a PostgREST list filter */
export const overdueFilter = () => ({
  "due_date@lt": today(),
  "status@in": "(draft,sent,partially_paid)",
});

export const formatAmount = (amount: number, currency: string) =>
  Number(amount ?? 0).toLocaleString("en-US", {
    style: "currency",
    currency: currency || "EGP",
  });

export type Suppression = RaRecord & {
  kind: "email" | "domain";
  value: string;
  reason?: string | null;
  source: "manual" | "unsubscribe" | "import";
  created_by?: Identifier | null;
  created_at: string;
};

/** A domain from a bare domain or any URL: lower case, no scheme, path, port or leading www. */
export const toDomain = (input: string) => {
  const text = input.trim().toLowerCase();
  if (!text) return null;
  try {
    const host = new URL(
      /^[a-z][a-z0-9+.-]*:\/\//.test(text) ? text : `http://${text}`,
    ).hostname;
    const domain = host.replace(/^www\./, "");
    return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(domain) ? domain : null;
  } catch {
    return null;
  }
};

/** What the do-not-contact list stores for a typed email, domain or website, or null when it is neither */
export const normaliseSuppression = (
  input: string,
): Pick<Suppression, "kind" | "value"> | null => {
  const text = input
    .trim()
    .toLowerCase()
    .replace(/^mailto:/, "");
  if (text.includes("@")) {
    const [local, host, ...rest] = text.split("@");
    const domain = toDomain(host ?? "");
    return local && domain && !rest.length && !/[\s/]/.test(local)
      ? { kind: "email", value: `${local}@${domain}` }
      : null;
  }
  const domain = toDomain(text);
  return domain ? { kind: "domain", value: domain } : null;
};

/** Per import row, an Error when any of its emails or domains is on the do-not-contact list, else null */
export const findSuppressedRows = (
  isSuppressed: (value: string) => Promise<boolean>,
  rows: (string | null | undefined)[][],
) =>
  Promise.all(
    rows.map(async (values) => {
      const list = values.filter((v): v is string => !!v?.trim());
      // ponytail: one call per value; a batch RPC if imports of thousands of rows get slow
      const hits = await Promise.all(list.map((v) => isSuppressed(v)));
      return hits.some(Boolean)
        ? new Error(`On the do-not-contact list: ${list.join(", ")}`)
        : null;
    }),
  );
